// frontend/src/SalesDashboardView/SalesDashboardView.jsx

import React from 'react';
import usePersistentLanguage from '../hooks/usePersistentLanguage';
import { useNavigate } from 'react-router-dom';
import styles from './SalesDashboardView.module.css';
import AppLayout from '../components/AppLayout';

// 1) Translation strings for English and Arabic
const translations = {
  en: {
    salesDashboard: 'Sales Dashboard',
    explanationText:
      'Welcome to the sales dashboard. Manage inventory, requests, and customer work from here.',
    viewProducts: 'View Inventory',
    makeRequest: 'Make Request',
    myRequests: 'My Requests',
    myReports: 'My Reports',
    manageCustomers: 'Manage Customers',
    workspaceCardTitle: 'Workspace',
    workspaceCardSubtitle: 'Access products quickly and raise precise requests.',
    trackingCardTitle: 'Tracking & Relations',
    trackingCardSubtitle: 'Follow up on existing requests and nurture customers.',
    // Button / Misc
    english: 'English',
    arabic: 'عربي',
    logout: 'Logout', // newly added for translation
  },
  ar: {
    salesDashboard: 'لوحة المبيعات',
    explanationText:
      'مرحبًا بك في لوحة المبيعات. أدر المخزون والطلبات والعملاء من هنا.',
    viewProducts: 'مخزون المنتجات',
    makeRequest: 'إنشاء طلب',
    myRequests: 'طلباتي',
    myReports: 'تقاريري',
    manageCustomers: 'إدارة العملاء',
    workspaceCardTitle: 'بيئة العمل',
    workspaceCardSubtitle: 'الوصول إلى المنتجات بسرعة وإنشاء الطلبات بدقة.',
    trackingCardTitle: 'المتابعة والعلاقات',
    trackingCardSubtitle: 'تابع الطلبات الحالية وطور علاقتك بالعملاء.',
    // Button / Misc
    english: 'English',
    arabic: 'عربي',
    logout: 'تسجيل الخروج', // newly added for translation
  },
};

const SalesDashboardView = () => {
  // 2) Language state and translation helper
  const [language, setLanguage] = usePersistentLanguage();
  const t = (key) => translations[language][key];

  // 3) Toggle language function
  const toggleLanguage = () => {
    setLanguage((prev) => (prev === 'en' ? 'ar' : 'en'));
  };

  const navigate = useNavigate();

  const handleViewProductsClick = () => navigate('/products-inventory');
  const handleMakeRequestClick = () => navigate('/sales-create-request');
  const handleViewMyRequests = () => navigate('/sales-my-requests');
  const handleViewMyReports = () => navigate('/sales-reports');
  const handleViewCustomers = () => navigate('/customers');

  return (
    <AppLayout
      title={t('salesDashboard')}
      language={language}
      languageLabel={language === 'en' ? t('arabic') : t('english')}
      onToggleLanguage={toggleLanguage}
      showLogout
      logoutLabel={t('logout')}
      contentClassName={styles.dashboardView}
    >
      <div className={styles.explanationText}>
        <p>{t('explanationText')}</p>
      </div>

      <div className={styles.buttonContainer}>
        <article className={styles.actionCard}>
          <h2 className={styles.cardHeading}>{t('workspaceCardTitle')}</h2>
          <p className={styles.cardSummary}>{t('workspaceCardSubtitle')}</p>
          <div className={styles.cardActions}>
            <button className={styles.actionButton} onClick={handleViewProductsClick}>
              <i className="fas fa-eye"></i> {t('viewProducts')}
            </button>
            <button className={`${styles.actionButton} ${styles.neutral}`} onClick={handleMakeRequestClick}>
              <i className="fas fa-envelope"></i> {t('makeRequest')}
            </button>
          </div>
        </article>

        <article className={styles.actionCard}>
          <h2 className={styles.cardHeading}>{t('trackingCardTitle')}</h2>
          <p className={styles.cardSummary}>{t('trackingCardSubtitle')}</p>
          <div className={styles.cardActions}>
            <button className={`${styles.actionButton} ${styles.accent}`} onClick={handleViewMyRequests}>
              <i className="fas fa-list"></i> {t('myRequests')}
            </button>
            <button className={`${styles.actionButton} ${styles.neutral}`} onClick={handleViewMyReports}>
              <i className="fas fa-chart-line"></i> {t('myReports')}
            </button>
            <button className={styles.actionButton} onClick={handleViewCustomers}>
              <i className="fas fa-user"></i> {t('manageCustomers')}
            </button>
          </div>
        </article>
      </div>
    </AppLayout>
  );
};

export default SalesDashboardView;
